import { useState } from 'react';
import Swal from 'sweetalert2';
import ModalBase from '@/components/base/ModalBase';
import {
  type Payment,
  methodLabels,
} from '@/mocks/superAdminPayments';

interface ManualPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onCreatePayment: (payment: Payment) => void;
  nextId: number;
}

type PaymentMethod = Payment['method'];

const METHODS: PaymentMethod[] = ['stripe', 'paypal', 'square', 'manual'];

const methodIcon: Record<PaymentMethod, string> = {
  stripe: 'ri-bank-card-line',
  paypal: 'ri-paypal-line',
  square: 'ri-apps-2-line',
  manual: 'ri-user-settings-line',
};

export default function ManualPaymentModal({ open, onClose, onCreatePayment, nextId }: ManualPaymentModalProps) {
  const [form, setForm] = useState({
    radioName: '',
    frequency: '',
    planName: 'Starter',
    amount: '',
    method: 'stripe' as PaymentMethod,
    reference: '',
    annualLicense: '',
    invoiceId: '',
    contractId: '',
    description: '',
  });

  const handleSubmit = () => {
    if (!form.radioName.trim() || !form.amount || !form.reference.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'Completá radio, monto y referencia como mínimo.',
        confirmButtonColor: '#1a1a1a',
        customClass: {
          popup: 'rounded-2xl text-sm',
          title: 'text-sm font-bold',
          confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        },
      });
      return;
    }
    const amountNum = parseFloat(form.amount);
    if (isNaN(amountNum) || amountNum < 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Ingresá un monto válido.',
        confirmButtonColor: '#1a1a1a',
        customClass: {
          popup: 'rounded-2xl text-sm',
          title: 'text-sm font-bold',
          confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        },
      });
      return;
    }

    const newPayment: Payment = {
      id: nextId,
      radioName: form.radioName,
      frequency: form.frequency || '—',
      planName: form.planName,
      amount: amountNum,
      method: form.method,
      status: 'pending',
      reviewed: false,
      date: new Date().toISOString().slice(0, 10),
      reference: form.reference,
      description: form.description || undefined,
      annualLicense: form.annualLicense || undefined,
      invoiceId: form.invoiceId || undefined,
      contractId: form.contractId || undefined,
      extras: [],
      processor: 'Procesador Manual',
    };

    onCreatePayment(newPayment);
    setForm({
      radioName: '', frequency: '', planName: 'Starter', amount: '',
      method: 'stripe', reference: '', annualLicense: '', invoiceId: '',
      contractId: '', description: '',
    });

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: `Pago ${newPayment.reference} registrado`,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl text-xs' },
    });
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Registrar Pago Manual"
      subtitle="Cargá un pago recibido por fuera de la plataforma"
      size="md"
      headerIcon={
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
          <i className="ri-add-circle-line text-amber-600 text-sm"></i>
        </div>
      }
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm mr-1"></i>Registrar Pago
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Radio *</label>
            <input
              type="text"
              value={form.radioName}
              onChange={(e) => setForm({ ...form, radioName: e.target.value })}
              placeholder="Nombre de la radio"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Frecuencia RFW</label>
            <input
              type="text"
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              placeholder="99.90 RFW"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Plan</label>
            <select
              value={form.planName}
              onChange={(e) => setForm({ ...form, planName: e.target.value })}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              <option>Starter</option>
              <option>Básico</option>
              <option>Pro</option>
              <option>Business</option>
              <option>Premium</option>
            </select>
          </div>
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Monto *</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Método *</label>
            <div className="relative">
              <select
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value as PaymentMethod })}
                className="w-full pl-8 pr-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>{methodLabels[m]}</option>
                ))}
              </select>
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className={`${methodIcon[form.method]} text-foreground-400 text-xs`}></i>
              </div>
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-[10px]"></i>
              </div>
            </div>
          </div>
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Referencia *</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="PAY-20260611-XXX"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors font-mono"
            />
          </div>
        </div>
        <div>
          <label className="text-foreground-400 text-[10px] font-medium block mb-1">Licencia Anual</label>
          <input
            type="text"
            value={form.annualLicense}
            onChange={(e) => setForm({ ...form, annualLicense: e.target.value })}
            placeholder="Licencia Anual de Frecuencia RFW — $49.95"
            className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Invoice</label>
            <input
              type="text"
              value={form.invoiceId}
              onChange={(e) => setForm({ ...form, invoiceId: e.target.value })}
              placeholder="INV-202606-XXX"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors font-mono"
            />
          </div>
          <div>
            <label className="text-foreground-400 text-[10px] font-medium block mb-1">Contrato</label>
            <input
              type="text"
              value={form.contractId}
              onChange={(e) => setForm({ ...form, contractId: e.target.value })}
              placeholder="CTR-2026-XXX"
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors font-mono"
            />
          </div>
        </div>
        <div>
          <label className="text-foreground-400 text-[10px] font-medium block mb-1">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Notas sobre este pago..."
            rows={2}
            className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none"
          />
        </div>
      </div>
    </ModalBase>
  );
}